import { Fragment, useMemo, type ReactNode } from 'react';
import { parseMarkdown, type BlockNode, type InlineNode } from './parse';
import { reportIssue } from '../telemetry';
import { cn } from '../utils';

/**
 * Renders the strict markdown AST as React elements.
 *
 * There is no `dangerouslySetInnerHTML` in this file and there must never be
 * one. The parser hands us a closed node union; we map each member to an
 * element. An HTML string is never constructed, so there is no sanitizer to
 * bypass.
 *
 * Duplicated in shape from client-dashboard/src/lib/markdown/Markdown.tsx.
 */

function renderInline(nodes: InlineNode[], keyPrefix: string): ReactNode {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case 'text':
        return <Fragment key={key}>{node.value}</Fragment>;
      case 'strong':
        return <strong key={key}>{renderInline(node.children, key)}</strong>;
      case 'em':
        return <em key={key}>{renderInline(node.children, key)}</em>;
      case 'del':
        return <del key={key}>{renderInline(node.children, key)}</del>;
      case 'code':
        return <code key={key}>{node.value}</code>;
      case 'link':
        return (
          <a key={key} href={node.href} target="_blank" rel="noopener noreferrer nofollow ugc">
            {renderInline(node.children, key)}
          </a>
        );
      case 'blocked':
        return <BlockedInline key={key} label={node.label} reason={node.reason} />;
      case 'break':
        return <br key={key} />;
      default: {
        // The union is closed, so this is unreachable by construction. It stays
        // because "unreachable by construction" stops being true the moment
        // someone adds a node type and forgets this file.
        const exhaustive: never = node;
        void exhaustive;
        return null;
      }
    }
  });
}

function BlockedInline({ label, reason }: { label: string; reason: 'scheme' | 'image' }) {
  if (reason === 'scheme') {
    reportIssue({ kind: 'blocked_link_scheme', token: 'inline_link', context: { label: label.slice(0, 40) } });
  }
  return (
    <span
      className="mx-[0.1em] inline-flex items-baseline gap-1 rounded-sm bg-sunken px-1 font-mono text-[0.85em] text-ink-3"
      title={
        reason === 'image'
          ? 'Images from an agent-read repository are not rendered.'
          : 'The link scheme is outside the https/mailto allowlist, so it was not made clickable.'
      }
    >
      {label || (reason === 'image' ? 'image' : 'link')}
    </span>
  );
}

function renderBlocks(blocks: BlockNode[], keyPrefix: string): ReactNode {
  return blocks.map((block, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (block.type) {
      case 'paragraph':
        return <p key={key}>{renderInline(block.children, key)}</p>;
      case 'heading': {
        const children = renderInline(block.children, key);
        if (block.level === 1) return <h1 key={key}>{children}</h1>;
        if (block.level === 2) return <h2 key={key}>{children}</h2>;
        return <h3 key={key}>{children}</h3>;
      }
      case 'list':
        return block.ordered ? (
          <ol key={key} start={block.start}>
            {block.items.map((item, itemIndex) => (
              <li key={`${key}-i${itemIndex}`}>{renderBlocks(item, `${key}-i${itemIndex}`)}</li>
            ))}
          </ol>
        ) : (
          <ul key={key}>
            {block.items.map((item, itemIndex) => (
              <li key={`${key}-i${itemIndex}`}>{renderBlocks(item, `${key}-i${itemIndex}`)}</li>
            ))}
          </ul>
        );
      case 'quote':
        return <blockquote key={key}>{renderBlocks(block.children, key)}</blockquote>;
      case 'codeBlock':
        return (
          <pre key={key}>
            <code>{block.value}</code>
          </pre>
        );
      case 'rule':
        return <hr key={key} />;
      default: {
        const exhaustive: never = block;
        void exhaustive;
        return null;
      }
    }
  });
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = useMemo(() => {
    try {
      return parseMarkdown(source);
    } catch {
      reportIssue({ kind: 'render_error', token: 'markdown_parse' });
      return null;
    }
  }, [source]);

  // A parse failure falls back to plain text, which is still safe: React
  // escapes text children. It never falls back to raw HTML.
  if (!blocks) {
    return <div className={cn('agent-prose whitespace-pre-wrap', className)}>{source}</div>;
  }

  return <div className={cn('agent-prose', className)}>{renderBlocks(blocks, 'b')}</div>;
}
